import { AddAlertOutlined } from '@mui/icons-material';
import {
  Button,
  type DialogProps,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Dialog, DialogContent, Show, useIsMobile } from '@side/pantry';
import {
  InfiniteScrollLoader,
  type InfiniteScrollLoaderProps,
} from '@/components/InfiniteScrollLoader';
import type { InteractionInformation } from '../types';

export interface InteractionSelectionModalProps
  extends DialogProps,
    InfiniteScrollLoaderProps {
  onClose: (eventTarget: string) => void;
  numItems: number;
  open: boolean;
  interactions: InteractionInformation[];
  modalSubtitle: string;
}

export const InteractionSelectionModal = ({
  interactions,
  loading,
  onClose,
  numItems,
  open,
  modalSubtitle,
  onLoadMore,
}: InteractionSelectionModalProps) => {
  const isMobile = useIsMobile();

  return (
    <Dialog
      maxWidth="sm"
      onClose={() => onClose('background')}
      open={open}
      sx={{ maxHeight: { xs: 'unset', md: '520px' }, my: 'auto' }}
    >
      <DialogContent
        title="Choose a Transaction"
        onClose={() => onClose('close_button')}
      >
        <Stack sx={{ gap: 3 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <AddAlertOutlined sx={{ paddingTop: 0.25 }} />
            <Typography variant="body1">{modalSubtitle}</Typography>
          </Stack>

          <Show
            when={interactions.length}
            fallback={
              <Stack
                sx={{
                  gap: 2,
                  pb: 0.25,
                  mx: { xs: 0, md: 4.5 },
                }}
                data-testid="interaction-selection-loading-skeleton"
              >
                {Array.from({ length: Math.min(numItems, 10) }).map(
                  (_, index) => (
                    <Skeleton
                      variant="rectangular"
                      // biome-ignore lint/suspicious/noArrayIndexKey: Skeletons do not move
                      key={index}
                      height={102}
                    />
                  ),
                )}
              </Stack>
            }
          >
            <Stack
              sx={{ flexDirection: 'column', gap: 2, paddingBottom: 0.25 }}
            >
              {interactions.map((interaction, index) => {
                const { onClick, title, subtitle, description, id } =
                  interaction;

                return (
                  <Paper
                    component={Button}
                    key={id}
                    onClick={() => onClick(index)}
                    name={interaction.elementId}
                    sx={{
                      textAlign: 'unset',
                      borderColor: 'other.outlineBorder',
                      borderRadius: 0.5,
                      borderStyle: 'solid',
                      borderWidth: '1.5px',
                      boxShadow: 'none',
                      marginX: isMobile ? undefined : 4.5,
                      padding: 2,
                    }}
                  >
                    <Stack direction="column" sx={{ width: '100%' }}>
                      <Typography
                        component="h3"
                        noWrap
                        sx={{ paddingBottom: 0.5 }}
                        variant="subtitle2"
                      >
                        {title}
                      </Typography>

                      <Typography
                        sx={{ color: 'text.secondary', paddingBottom: 1.5 }}
                        variant="body3"
                      >
                        {subtitle}
                      </Typography>

                      <Typography
                        sx={{ color: 'text.secondary' }}
                        variant="body3"
                      >
                        {description}
                      </Typography>
                    </Stack>
                  </Paper>
                );
              })}
              <InfiniteScrollLoader onLoadMore={onLoadMore} loading={loading} />
            </Stack>
          </Show>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
